import { useForm } from "@mantine/form"
import {
  ArrowLeft,
  Bell,
  Files,
  MapPin,
  Pencil,
  Plus,
  Users,
} from "@phosphor-icons/react"
import {
  Breadcrumbs,
  Button,
  Card,
  Checkbox,
  Divider,
  SpinnerOverlay,
  Tag,
  Tooltip,
} from "@vesatogo/grass-core"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { useState } from "react"
import "react-circular-progressbar/dist/styles.css"
import toast from "react-hot-toast"
import { Link, useNavigate, useParams } from "react-router-dom"
import { AgreementStatusCard } from "~/components/Agreement/AgreementStatusCard"
import { RenewAgreement } from "~/components/Agreement/RenewAgreement"
import { AddSubLocationModal } from "~/components/StorageLocation/AddSubLocationModal"
import { TransactionForm } from "~/components/Transaction/TransactionForm"
import { TransactionHistoryModal } from "~/components/Transaction/TransactionHistoryModal"
import { ALERTS } from "~/constants/Alerts"
import { AppRoutes } from "~/constants/routes"
import { useStorageLocationDetailQuery } from "~/generated/graphql"

const StorageLocationDetail = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [showSubLocationModal, setShowSubLocationModal] = useState(false)
  const [renewModal, setRenewModal] = useState(false)
  const [showTransactionModal, setShowTransactionModal] = useState(false)
  const [showTransactionHistoryModal, setShowTransactionHistoryModal] =
    useState(false)
  const form = useForm<any>({
    initialValues: {
      transaction_attachments: [],
      attachments: [],
    },
  })
  const { values: state, getInputProps, setValues: setState, reset } = form

  const [{ data, fetching }, refetch] = useStorageLocationDetailQuery({
    variables: {
      id: id as string,
    },
    pause: !id,
    requestPolicy: "cache-and-network",
  })

  function getFileUploadProps(
    key: string,
    maxFiles = 4,
    shouldEncrypt = false
  ) {
    const inputProps = form.getInputProps(key)
    return {
      media: inputProps.value,
      onChange: inputProps.onChange,
      maxFiles,
      shouldEncrypt,
      onError: toast.error,
    }
  }
  const storageLocation = data?.storage_location_by_pk
  // const storageLocation = storageLocation?.
  const previousAgreements = data?.storage_location_by_pk?.previous_agreements
  const pendingAgreement = data?.storage_location_by_pk?.pending_agreement?.[0]
  const currentAgreement =
    data?.storage_location_by_pk?.current_agreement?.[0] ||
    pendingAgreement ||
    previousAgreements?.[0]
  const daysToExpire = dayjs(currentAgreement?.end_date)
    .startOf("day")
    .diff(dayjs().startOf("day"), "day")
  const currentAgreementStatus =
    daysToExpire <= 7 &&
    daysToExpire > 0 &&
    currentAgreement?.is_verified &&
    currentAgreement?.is_active
      ? "Expiring"
      : daysToExpire >= 0 &&
        currentAgreement?.is_verified &&
        currentAgreement?.is_active
      ? "Active"
      : daysToExpire < 0 && currentAgreement?.is_verified
      ? "Expired"
      : currentAgreement?.is_active
      ? "Active"
      : !currentAgreement?.is_active && currentAgreement?.is_verified
      ? "Inactive"
      : "Pending Verification"

  const capacityUsage = {
    occupied_percentage: Number(
      (storageLocation?.trades_aggregate?.aggregate?.sum?.deducted_quantity_kg /
        storageLocation?.capacity) *
        100
    ).toFixed(1),
    used_space:
      storageLocation?.trades_aggregate?.aggregate?.sum?.deducted_quantity_kg,
    remaining_space:
      storageLocation?.capacity -
      storageLocation?.trades_aggregate?.aggregate?.sum?.deducted_quantity_kg,
    sub_locations: storageLocation?.storage_sub_locations?.map(sub => {
      return {
        name: sub?.name,
        capacity: sub?.capacity + " " + sub?.capacity_unit?.name,
        storage_type: sub?.storage_location_kind?.name,
        code: sub?.codename,
        used_capacity:
          sub?.trades_aggregate?.aggregate?.sum?.deducted_quantity_kg,
        utilization_percentage:
          (sub?.trades_aggregate?.aggregate?.sum?.deducted_quantity_kg /
            sub?.capacity) *
          100,
      }
    }),
  }

  const isExpired = dayjs(currentAgreement?.end_date).isBefore(dayjs())
  const isApproved = currentAgreement?.is_verified
  const isDisabled = isExpired
  return (
    <div className="flex flex-col min-h-screen bg-gray-50 pb-20">
      <header className="bg-white p-3 border-b border-gray-200 flex items-center gap-4">
        <SpinnerOverlay show={fetching} />
        <button
          onClick={() => navigate(-1)}
          className="p-1 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <Breadcrumbs
          items={[
            {
              text: "Storage Location",
              link: AppRoutes.storageLocations,
              isActive: false,
            },
            {
              text: `Storage Location ${id}`,
              link: AppRoutes.storageLocationDetail(id as any),
              isActive: true,
            },
          ]}
          linkRenderer={(link, item) => {
            return <Link to={link as any}>{item}</Link>
          }}
        />
      </header>

      <main className="max-w-7xl mx-auto w-full p-4 bg-white mt-6 rounded-xl shadow-sm relative">
        <div className="bg-gray-100 border-gray-300 border rounded-xl p-6 flex flex-col gap-6">
          {/* Storage Info Section */}
          <section className="flex flex-col gap-4">
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-2">
                <Files size={20} /> Storage Info
              </div>
              <div className="flex gap-4 items-center">
                <span>
                  {currentAgreement ? (
                    <Tag
                      intent={
                        currentAgreementStatus === "Pending Verification"
                          ? "info"
                          : currentAgreementStatus === "Active"
                          ? "primary"
                          : currentAgreementStatus === "Expired"
                          ? "danger"
                          : currentAgreementStatus === "Inactive"
                          ? "danger"
                          : "warning"
                      }
                      className="!rounded-xl"
                    >
                      {currentAgreementStatus}
                    </Tag>
                  ) : undefined}
                </span>
                <span className="text-gray-400">|</span>
                <Button
                  variant="minimal"
                  // disabled={isDisabled}
                  className="flex items-center gap-2 cursor-pointer text-sm text-green-400"
                  onClick={() => {
                    navigate(
                      `${AppRoutes.storageLocationDetail(id as any)}/edit`
                    )
                  }}
                  size="sm"
                  leftIcon={<Pencil size={15} />}
                >
                  Storage-Location
                </Button>
                <Button
                  size="sm"
                  leftIcon={<Plus />}
                  onClick={() => setShowSubLocationModal(true)}
                >
                  Sub-Location
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-3 text-[14px]">
              <div className="flex flex-col gap-1">
                <span className="">Storage Location Name</span>
                <span className="text-gray-600">{storageLocation?.name}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="">Storage Location Code</span>
                <Tag variant="outline" intent="info" className="!rounded-xl">
                  {storageLocation?.codename}
                </Tag>
              </div>
              {/* <div className="flex flex-col gap-1">
                <span className="">Storage Type</span>
                <span className="text-gray-600">
                  {storageLocation?.}
                </span>
              </div> */}
              <div className="flex flex-col gap-1">
                <span className="">Capacity</span>
                <span className="text-brown-400">
                  {storageLocation?.capacity}
                </span>
              </div>
            </div>

            <div className="border border-gray-300 bg-gray-200 flex flex-col gap-2  p-4 rounded-lg text-sm text-gray-800">
              <div className="grid grid-cols-4 gap-1 text-sm">
                <span className="">Name</span>
                <span className="">Storage Type</span>
                <span className="">Code</span>
                <span className="">Capacity</span>
              </div>
              {storageLocation?.storage_sub_locations?.map((sub: any, idx) => {
                return (
                  <div
                    key={idx}
                    className="flex flex-col gap-1 grid grid-cols-4"
                  >
                    <span className="">{idx + 1 + ". " + sub?.name}</span>
                    <span className="flex flex-col ">
                      <span className="text-brown-400">
                        {sub?.storage_location_kind?.name}
                      </span>
                    </span>
                    <span className="flex flex-col ">
                      <Tooltip content={sub?.codename}>
                        <Tag
                          className="!rounded-xl"
                          variant="outline"
                          intent="info"
                          size="sm"
                        >
                          {sub?.codename?.slice(0, 20) + "..."}
                        </Tag>
                      </Tooltip>
                    </span>

                    <span className="flex flex-col ">
                      <span className="text-brown-400">{sub?.capacity}</span>
                    </span>
                  </div>
                )
              })}
            </div>
          </section>

          {/* Address Details Section */}
          <section className="">
            <h2 className="font-500 text-gray-800 mb-6 flex items-center gap-2">
              <MapPin size={20} /> Address Details
            </h2>
            <div className="grid grid-cols-4 gap-8 text-sm">
              <div className="flex flex-col gap-1 col-span-4">
                <span className="">Full Address</span>
                <span className="text-gray-600">
                  {storageLocation?.address?.raw_address}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="">Village</span>
                <span className="text-gray-600">
                  {storageLocation?.address?.village}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="">Taluka</span>
                <span className="text-gray-600">
                  {storageLocation?.address?.taluka}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="">District</span>
                <span className="text-gray-600">
                  {storageLocation?.address?.district}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="">Pincode</span>
                <span className="text-gray-600">
                  {storageLocation?.address?.pincode}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="">State</span>
                <span className="text-gray-600">
                  {storageLocation?.address?.state}
                </span>
              </div>
            </div>
          </section>

          {/* Usage and Agreement Row */}
          <div className="grid grid-cols-2 gap-6 items-start">
            {/* Capacity Usage */}
            {/* <Card className="p-6">
              <h2 className="text-md font-700 text-gray-800 mb-6">
                Capacity Usage
              </h2>
              <div className="flex flex-col items-center gap-6">
                <div className="w-48 h-48">
                  <CircularProgressbar
                    value={Number(capacityUsage?.occupied_percentage) || 0}
                    text={`${capacityUsage?.occupied_percentage || 0}%`}
                    styles={buildStyles({
                      pathColor: "#799700",
                      textColor: "#111827",
                      trailColor: "#f3f4f6",
                      textSize: "24px",
                    })}
                    counterClockwise
                  />
                </div>

                <div className="w-full flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between text-xs font-500">
                      <span className="text-gray-500">Used Space</span>
                      <span className="text-gray-800">
                        {capacityUsage?.used_space}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-300 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-400"
                        style={{
                          width: `${capacityUsage?.occupied_percentage}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between text-xs font-500">
                      <span className="text-gray-500">Remaining</span>
                      <span className="text-gray-800">
                        {capacityUsage?.remaining_space}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col gap-4">
                    {capacityUsage?.sub_locations?.map((sub: any, idx) => {
                      const item = {
                        name: sub?.name,
                        used: sub?.used_space
                          ? sub?.used_space.split(" ")[0]
                          : 0,
                        total: sub?.capacity ? sub?.capacity.split(" ")[0] : 0,
                        util: sub?.utilization
                          ? sub?.utilization.split(" ")[0]
                          : 0,
                        color:
                          sub?.utilization === "100%"
                            ? "bg-orange-500"
                            : "bg-green-500",
                      }

                      return (
                        <div key={idx} className="flex flex-col gap-1">
                          <div className="flex justify-between items-end">
                            <div className="flex flex-col">
                              <span className="text-sm font-600 text-gray-800">
                                {idx + 1}. {item.name}
                              </span>
                              <span className="text-gray-600 text-sm">
                                <b className="text-gray-800 text-2xl ">
                                  {item.used}
                                </b>{" "}
                                sq ft
                              </span>
                              <span className="text-sm text-gray-600">
                                Used of {item.total} total capacity
                              </span>
                            </div>
                            <span
                              className={`text-md font-700 ${
                                item.util === "100%"
                                  ? "text-orange-500"
                                  : "text-green-500"
                              }`}
                            >
                              {item.util}
                            </span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${item.color}`}
                              style={{ width: item.util }}
                            />
                          </div>
                          <p className="text-[8px] text-gray-400 self-end  font-600">
                            Utilization
                          </p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </Card> */}

            {/* Agreement Details */}
            <Card className="p-6">
              <h2 className="text-md font-700 text-gray-800 mb-6 ">
                Agreement Details
              </h2>
              {currentAgreement ? (
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-3 border border-gray-300 p-3 bg-gray-100 rounded-lg">
                    <div className="flex justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-white border border-gray-300 flex items-center justify-center text-gray-400">
                          <Users weight="fill" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500 ">
                            Agreement Person Name
                          </span>
                          <span className="text-sm font-600 text-gray-800">
                            {currentAgreement?.name}
                          </span>
                        </div>
                      </div>
                      <Tag
                        variant="minimal"
                        intent="info"
                        className="!rounded-xl"
                      >
                        {currentAgreement?.ownership_kind === "Lease"
                          ? "Rent"
                          : currentAgreement?.ownership_kind}
                      </Tag>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-600  ">
                          Rent Amount
                        </span>
                        <span className="text-lg font-700 text-green-400">
                          {inr(currentAgreement?.lease_amount || 0)}
                        </span>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-xs text-gray-600  ">
                          Next Payment
                        </span>
                        <span className="text-xs font-700 text-gray-800">
                          {storageLocation?.summary?.next_payment_date}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center border-b">
                      <p className="text-xs cursor-pointer">
                        Transaction History
                      </p>
                      <div className="flex gap-4 justify-between items-center">
                        <p
                          className="text-xs text-green cursor-pointer"
                          onClick={() => setShowTransactionHistoryModal(true)}
                        >
                          Transaction Records
                        </p>
                        <Divider vertical />
                        <p
                          onClick={() => {
                            if (isDisabled) return
                            setShowTransactionModal(true)
                          }}
                          className={classNames(
                            "text-xs text-green flex gap-1 items-center cursor-pointer",
                            isDisabled && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <Plus />
                          Transaction
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1 border border-gray-300 p-4 rounded-lg">
                      <span className="text-xs text-gray-500 ">Start Date</span>
                      <span className="text-sm font-600">
                        {dayjs(currentAgreement?.start_date).format(
                          "DD MMM, YYYY"
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1 border border-gray-300 p-4 rounded-lg">
                      <span className="text-xs text-gray-500">End Date</span>
                      <span className="text-sm font-600">
                        {dayjs(currentAgreement?.end_date).format(
                          "DD MMM, YYYY"
                        )}
                      </span>
                    </div>
                  </div>

                  {currentAgreement !== pendingAgreement &&
                    currentAgreement !== previousAgreements?.[0] && (
                      <AgreementStatusCard
                        name={currentAgreement?.name}
                        agreement={currentAgreement}
                        attachments={currentAgreement?.attachments || []}
                        status={currentAgreementStatus}
                        statusColor={
                          currentAgreementStatus === "Active"
                            ? "text-green"
                            : "text-red"
                        }
                      />
                    )}

                  <Button
                    text={"Renew Agreement"}
                    onClick={() => setRenewModal(true)}
                    hidden={daysToExpire > 7 || Boolean(pendingAgreement)}
                  />

                  {pendingAgreement ? (
                    <AgreementStatusCard
                      name={pendingAgreement?.name}
                      attachments={pendingAgreement?.attachments || []}
                      status="Sent for Approval"
                      statusColor="text-blue"
                      agreement={pendingAgreement}
                    />
                  ) : null}

                  {previousAgreements?.length ? (
                    <p className="text-sm font-600 text-gray-800 ">
                      Previous Agreement:
                    </p>
                  ) : null}
                  {previousAgreements?.map(prev => {
                    const status =
                      daysToExpire <= 7 &&
                      daysToExpire > 0 &&
                      prev?.is_verified &&
                      prev?.is_active
                        ? "Expiring"
                        : daysToExpire >= 0 &&
                          prev?.is_verified &&
                          prev?.is_active
                        ? "Active"
                        : daysToExpire < 0 && prev?.is_verified
                        ? "Expired"
                        : prev?.is_active
                        ? "Active"
                        : !prev?.is_active && prev?.is_verified
                        ? "Inactive"
                        : "Pending Verification"
                    return (
                      <AgreementStatusCard
                        key={prev?.id}
                        name={prev?.name}
                        status={status}
                        agreement={prev}
                        statusColor={
                          status === "Pending Verification"
                            ? "text-blue"
                            : status === "Active"
                            ? "text-green"
                            : status === "Expired"
                            ? "text-red"
                            : status === "Inactive"
                            ? "text-red"
                            : "text-yellow"
                        }
                        attachments={prev?.attachments || []}
                        className="bg-gray-100 border-gray-200"
                      />
                    )
                  })}

                  <p className="text-center text-[10px]  text-gray-500 pt-2">
                    {daysToExpire > 0
                      ? `Agreement expires in ${daysToExpire} days`
                      : daysToExpire === 0
                      ? "Agreement expires today"
                      : `Agreement Expired ${Math.abs(daysToExpire)} days ago`}
                  </p>
                </div>
              ) : (
                <div>No Agreements Found!</div>
              )}
            </Card>

            <Card
              className={classNames("p-6", currentAgreement ? "" : "hidden")}
            >
              <div className="flex flex-col gap-4 ">
                <div className="p-4 border-gray-300 border rounded-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <Bell size={20} />
                    <h3 className="font-500 text-gray-800">Alert</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    When you want to receive notifications before the agreement
                    expires.
                  </p>

                  <div className="flex flex-col gap-3 w-full">
                    {ALERTS?.filter(
                      alert =>
                        alert.days === currentAgreement?.expiry_alert_days
                    ).map(alert => {
                      const isSelected =
                        alert.days ===
                        (currentAgreement?.expiry_alert_days || 0)
                      return (
                        <div
                          key={alert.id}
                          onClick={() => {
                            form.setFieldValue("alert", alert)
                          }}
                          className={classNames(
                            "p-3 border rounded border-gray-300 rounded-lg flex flex-col gap-1 cursor-pointer",
                            isSelected ? "border-green-150 bg-green-100" : ""
                          )}
                        >
                          <div className="flex items-baseline gap-2 ">
                            <Checkbox checked={isSelected} disabled />
                            <div className="text-sm flex flex-col">
                              <span className="font-600">{alert.label}</span>
                              <span className="text-xs text-gray-600">
                                {alert.description}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Alert Settings Section */}
        </div>
      </main>
      <TransactionForm
        getInputProps={getInputProps}
        attachments={state.transaction_attachments || []}
        setFieldValue={form.setFieldValue}
        getFileUploadProps={getFileUploadProps}
        isOpen={showTransactionModal}
        onClose={() => setShowTransactionModal(false)}
        agreementId={currentAgreement?.id || ""}
        refetch={refetch}
        reset={reset}
      />
      <TransactionHistoryModal
        isOpen={showTransactionHistoryModal}
        onClose={() => setShowTransactionHistoryModal(false)}
        transactions={currentAgreement?.agreement_transactions || []}
      />
      <RenewAgreement
        isOpen={renewModal}
        onClose={() => setRenewModal(false)}
        getFileUploadProps={getFileUploadProps}
        getInputProps={getInputProps}
        reset={reset}
        parentData={{
          name: storageLocation?.name || "",
          capacity: Number(storageLocation?.capacity) || 0,
          capacity_unit_id: storageLocation?.capacity_unit?.id || "",
          agreementDate: currentAgreement?.end_date,
        }}
        state={state}
        storageLocationId={id as string}
        refetch={refetch}
        setFieldValue={form.setFieldValue}
      />
      <AddSubLocationModal
        isOpen={showSubLocationModal}
        onClose={() => setShowSubLocationModal(false)}
        storageLocationId={id as string}
        parentData={{
          name: storageLocation?.name || "",
          capacity: Number(storageLocation?.capacity) || 0,
          capacity_unit_id: storageLocation?.capacity_unit?.id || "",
        }}
        refetch={refetch}
        reset={reset}
      />
    </div>
  )
}

export default StorageLocationDetail
